import { type MouseEvent, useCallback, useMemo, useRef } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Tooltip } from "@mui/material";
import type {
	ColDef,
	ICellRendererParams,
	RowClickedEvent,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import type { PurchaseOrder } from "../models/PurchaseOrders";

interface Props {
	purchaseOrders: PurchaseOrder[];
	selectedId: number | null;
	onSelect: (po: PurchaseOrder) => void;
	onEdit: (po: PurchaseOrder) => void;
	onDelete: (po: PurchaseOrder) => void;
	writeDisabled: boolean;
	writeDisabledReason: string;
	showDeleted: boolean;
	loading?: boolean;
}

interface ActionsCellProps extends ICellRendererParams<PurchaseOrder> {
	onEdit: (po: PurchaseOrder) => void;
	onDelete: (po: PurchaseOrder) => void;
	writeDisabled: boolean;
	writeDisabledReason: string;
}

// Inline row actions (pinned right): Edit opens the PO modal, Delete asks for
// confirmation. No legacy in-cell editing — a click just opens the modal.
const ActionsCell = ({
	data,
	onEdit,
	onDelete,
	writeDisabled,
	writeDisabledReason,
}: ActionsCellProps) => {
	if (!data) return null;
	const stop = (e: MouseEvent) => e.stopPropagation();
	// A soft-deleted order is fully read-only — both Edit and Delete are off.
	// Driven by the IsDeleted bit (new SP); falls back to DateDeleted in
	// legacy mode. These rows only appear when "Show deleted" is on.
	const isDeleted = data.IsDeleted ?? data.DateDeleted != null;
	const actionsDisabled = writeDisabled || isDeleted;
	const disabledReason = isDeleted
		? "This order is deleted (read-only)"
		: writeDisabledReason;
	return (
		<Box
			sx={{
				display: "flex",
				gap: 0.5,
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
			}}
		>
			<Tooltip title={actionsDisabled ? disabledReason : "Edit order"}>
				<span>
					<IconButton
						size="small"
						sx={{ p: 0.25 }}
						disabled={actionsDisabled}
						onClick={(e) => {
							stop(e);
							onEdit(data);
						}}
						aria-label="edit order"
					>
						<EditIcon
							fontSize="small"
							color={actionsDisabled ? "disabled" : "primary"}
						/>
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip title={actionsDisabled ? disabledReason : "Delete order"}>
				<span>
					<IconButton
						size="small"
						sx={{ p: 0.25 }}
						disabled={actionsDisabled}
						onClick={(e) => {
							stop(e);
							onDelete(data);
						}}
						aria-label="delete order"
					>
						<DeleteIcon
							fontSize="small"
							color={actionsDisabled ? "disabled" : "error"}
						/>
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);
};

export const PurchaseOrdersTable = ({
	purchaseOrders,
	selectedId,
	onSelect,
	onEdit,
	onDelete,
	writeDisabled,
	writeDisabledReason,
	showDeleted,
	loading = false,
}: Props) => {
	const gridRef = useRef<AgGridReact<PurchaseOrder>>(null);

	const columnDefs = useMemo<ColDef<PurchaseOrder>[]>(
		() => [
			{ field: "Number", headerName: "Order #", width: 130 },
			{
				field: "RequisitionNumber",
				headerName: "Requisition #",
				width: 130,
			},
			{ field: "SupplierName", headerName: "Supplier", flex: 1, minWidth: 160 },
			{
				field: "CostCenterName",
				headerName: "Cost Center",
				flex: 1,
				minWidth: 150,
			},
			{
				field: "OrderStatusName",
				headerName: "Status",
				width: 140,
			},
			{
				field: "DateOrdered",
				headerName: "Date Ordered",
				width: 140,
				cellStyle: { textAlign: "center" },
				sort: "desc",
				valueFormatter: (params) => formatProxyDate(params.value),
			},
			// Only relevant when "Show deleted" is on — otherwise the column would
			// always be empty (no deleted rows in the default view).
			...(showDeleted
				? [
						{
							field: "DateDeleted",
							headerName: "Date Deleted",
							width: 140,
							cellStyle: { textAlign: "center" },
							valueFormatter: (params) => formatProxyDate(params.value),
						} as ColDef<PurchaseOrder>,
					]
				: []),
			{
				field: "ItemCount",
				headerName: "Items",
				width: 90,
				cellStyle: { textAlign: "center" },
			},
			{
				headerName: "",
				colId: "actions",
				pinned: "right",
				width: 96,
				sortable: false,
				filter: false,
				resizable: false,
				cellRenderer: ActionsCell,
				cellRendererParams: {
					onEdit,
					onDelete,
					writeDisabled,
					writeDisabledReason,
				},
			},
		],
		[onEdit, onDelete, writeDisabled, writeDisabledReason, showDeleted],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<PurchaseOrder>) => {
			if (event.data) onSelect(event.data);
		},
		[onSelect],
	);

	const getRowId = useCallback(
		(params: { data: PurchaseOrder }) => params.data.Id.toString(),
		[],
	);

	return (
		<div style={{ height: "100%", width: "100%" }}>
			<AgGridReact<PurchaseOrder>
				ref={gridRef}
				rowData={purchaseOrders}
				columnDefs={columnDefs}
				defaultColDef={globalDefaultColDef}
				loading={loading}
				onRowClicked={onRowClicked}
				getRowId={getRowId}
				rowSelection="single"
				suppressContextMenu
				animateRows={false}
				rowHeight={32}
				headerHeight={32}
				getRowStyle={(params) =>
					selectedId === params.data?.Id
						? { backgroundColor: "#e3f2fd" }
						: undefined
				}
			/>
		</div>
	);
};
